import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/sonner-toast';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthContext, type AuthContextType } from '@/hooks/useAuthContext';
import { clearAdminMfaState } from '@/lib/adminMfa';
import { resetInternalMfaSessionArtifacts } from '@/lib/internalMfaChallengeSession';
import { authDebug, isAuthDebugEnabled } from '@/lib/authDebugInstrumentation';
import { closeMyPanelSession } from '@/services/panelSession';
import { panelSessionKeys } from '@/hooks/security/usePanelSessionState';
import { favoritesKeys } from '@/domain/favorites/queryKeys';
import {
  normalizeFavoriteIds,
  persistFavoriteIdsSeed,
  readFavoriteIdsSeed,
} from '@/hooks/useFavoriteIds';

type AuthStatus = AuthContextType['authStatus'];

function resolveAuthStatus(user: User | null | undefined): AuthStatus {
  return user ? 'authenticated' : 'unauthenticated';
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  type AuthState = { user: User | null; session: Session | null; loading: boolean; authStatus: AuthStatus };
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    authStatus: 'bootstrapping',
  });
  const lastSessionTokenRef = useRef<string | null>(null);
  const stateSnapshotRef = useRef<AuthState>(state);
  const queryClient = useQueryClient();
  const authProviderRenderRef = useRef(0);
  authProviderRenderRef.current++;

  if (isAuthDebugEnabled()) {
    authDebug('AuthProvider.render', {
      n: authProviderRenderRef.current,
      loading: state.loading,
      hasUser: Boolean(state.user?.id),
      authStatus: state.authStatus,
    });
  }

  stateSnapshotRef.current = state;

  useEffect(() => {
    if (!isAuthDebugEnabled()) return;
    authDebug('AuthProvider.state_change', {
      loading: state.loading,
      userIdPresent: Boolean(state.user?.id),
      authStatus: state.authStatus,
    });
    authDebug('AuthProvider.auth_status_change', { authStatus: state.authStatus });
  }, [state.authStatus, state.loading, state.user?.id]);

  useEffect(() => {
    authDebug('AuthProvider.component_mount', {});
    return () => {
      authDebug('AuthProvider.component_unmount', {});
    };
  }, []);

  const hydrateFavoritesFromStorage = useCallback((userId: string | null | undefined) => {
    if (!userId) return;
    try {
      queryClient.setQueryData(favoritesKeys.ids(userId), readFavoriteIdsSeed());
    } catch {
      // ignore parse errors
    }
  }, [queryClient]);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        authDebug('AuthProvider.initializeAuth.start');
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        if (!mounted) return;
        const token = initialSession?.access_token ?? null;
        lastSessionTokenRef.current = token;
        setState(prev => {
          authDebug('AuthProvider.state_before', {
            source: 'initializeAuth',
            loading: prev.loading,
            userIdPresent: Boolean(prev.user?.id),
          });
          if (
            prev.session?.access_token === initialSession?.access_token
            && prev.user?.id === initialSession?.user?.id
            && prev.loading === false
            && prev.authStatus === resolveAuthStatus(initialSession?.user)
          ) {
            authDebug('AuthProvider.state_after', {
              source: 'initializeAuth_noop',
              loading: prev.loading,
              userIdPresent: Boolean(prev.user?.id),
            });
            return prev;
          }
          const next: AuthState = {
            user: initialSession?.user ?? null,
            session: initialSession ?? null,
            loading: false,
            authStatus: resolveAuthStatus(initialSession?.user),
          };
          authDebug('AuthProvider.state_after', {
            source: 'initializeAuth',
            loading: next.loading,
            userIdPresent: Boolean(next.user?.id),
          });
          return next;
        });
        if (initialSession?.user?.id) {
          hydrateFavoritesFromStorage(initialSession.user.id);
        }
        authDebug('AuthProvider.initializeAuth.done', {
          hasSession: Boolean(initialSession?.access_token),
        });
      } catch {
        if (!mounted) return;
        authDebug('AuthProvider.initializeAuth.done', {
          hasSession: false,
          error: true,
        });
        setState({ user: null, session: null, loading: false, authStatus: 'error' });
      }
    };

    async function prefetchFavorites(session: Session | null) {
      try {
        if (!session?.user?.id) return;

        const { data, error } = await supabase.rpc('get_my_favorite_property_ids');
        if (error) return;

        const ids = normalizeFavoriteIds(data);
        persistFavoriteIdsSeed(ids);
        queryClient.setQueryData(favoritesKeys.ids(session.user.id), ids);
      } catch {}
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;

      if (isAuthDebugEnabled()) {
        authDebug('AuthProvider.onAuthStateChange', {
          event,
          nextHasSession: Boolean(nextSession?.access_token),
        });
      }

      if (event === 'SIGNED_OUT') {
        const prevSnapshot = stateSnapshotRef.current;
        authDebug('AuthProvider.state_before', {
          source: 'auth_SIGNED_OUT',
          loading: prevSnapshot.loading,
          userIdPresent: Boolean(prevSnapshot.user?.id),
        });
        lastSessionTokenRef.current = null;
        resetInternalMfaSessionArtifacts();
        setState({ user: null, session: null, loading: false, authStatus: 'unauthenticated' });
        authDebug('AuthProvider.state_after', {
          source: 'auth_SIGNED_OUT',
          loading: false,
          userIdPresent: false,
        });
        return;
      }

      const nextToken = nextSession?.access_token ?? null;
      if (lastSessionTokenRef.current === nextToken) {
        setState(prev => {
          authDebug('AuthProvider.state_before', {
            source: `auth_${event}_same_token`,
            loading: prev.loading,
            userIdPresent: Boolean(prev.user?.id),
          });
          const next: AuthState = prev.loading
            ? {
                ...prev,
                loading: false,
                authStatus: resolveAuthStatus(prev.user),
              }
            : prev;
          authDebug('AuthProvider.state_after', {
            source: `auth_${event}_same_token`,
            loading: next.loading,
            userIdPresent: Boolean(next.user?.id),
          });
          return next;
        });
        return;
      }
      lastSessionTokenRef.current = nextToken;

      setState(prev => {
        authDebug('AuthProvider.state_before', {
          source: `auth_${event}`,
          loading: prev.loading,
          userIdPresent: Boolean(prev.user?.id),
        });
        const next: AuthState = {
          user: nextSession?.user ?? null,
          session: nextSession ?? null,
          loading: false,
          authStatus: resolveAuthStatus(nextSession?.user),
        };
        authDebug('AuthProvider.state_after', {
          source: `auth_${event}`,
          loading: next.loading,
          userIdPresent: Boolean(next.user?.id),
        });
        return next;
      });
      if (event === 'SIGNED_IN') {
        prefetchFavorites(nextSession);
        hydrateFavoritesFromStorage(nextSession?.user?.id ?? null);
      }
      if (event === 'TOKEN_REFRESHED') {
        hydrateFavoritesFromStorage(nextSession?.user?.id ?? null);
      }
    });
    authDebug('AuthProvider.subscription.created', {});

    initializeAuth();
    return () => {
      mounted = false;
      authDebug('AuthProvider.subscription.removed', {});
      subscription.unsubscribe();
    };
  }, [hydrateFavoritesFromStorage, queryClient]);

  const signOut = useCallback(async () => {
    try {
      await closeMyPanelSession();
    } catch (error) {
      console.error('No se pudo cerrar la panel session antes de signOut', error);
    }

    setState({ user: null, session: null, loading: false, authStatus: 'unauthenticated' });
    lastSessionTokenRef.current = null;
    try {
      await supabase.auth.signOut();
    } catch {
      // no-op
    } finally {
      try { toast.success('Sesión cerrada', { description: 'Cerraste sesión correctamente.' }); } catch {}
      try { localStorage.removeItem('rc:postAuthRedirect'); } catch {}
      try { localStorage.removeItem('rc:postAuthNext'); } catch {}
      try { localStorage.removeItem('rc:intendedRole'); } catch {}
      try { localStorage.removeItem('rc:intendedPhone'); } catch {}
      try { sessionStorage.removeItem('rc:favIds'); } catch {}
      try { localStorage.removeItem('rc:favIds'); } catch {}
      clearAdminMfaState();
      resetInternalMfaSessionArtifacts();
      try {
        queryClient.removeQueries({ queryKey: favoritesKeys.all });
        queryClient.removeQueries({ queryKey: panelSessionKeys.all });
      } catch {}
    }
  }, [queryClient]);

  const value = useMemo<AuthContextType>(() => ({
    user: state.user,
    session: state.session,
    loading: state.loading,
    isAuthenticated: Boolean(state.user),
    authStatus: state.authStatus,
    signOut,
  }), [state.user, state.session, state.loading, state.authStatus, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
